/**
 * `color` widget. A color is either a literal sRGB triple (edited with the
 * native color picker) or bound to a theme token such as `keyLightTint`, so
 * it follows theme switches, like Unreal's material parameter collections.
 * Mixed, the menu shows a dash and there is no one color to pick from.
 */
import { parseHexRgb, rgbToHex } from "../../core/color";
import type { Rgb } from "../../core/color";
import { cssName } from "../theme/flatten";
import { FieldError, fieldAria } from "./fieldAria";
import { isMixed, MIXED_OPTION, MixedOption } from "./mixed";
import type { WidgetProps } from "./types";
import { useWidgetEnvironment } from "./WidgetEnvironment";

/** The literal color chosen when unbinding from a theme token. */
const CUSTOM_DEFAULT: Rgb = [1, 1, 1];

export function ColorWidget({ id, label, prop, value, onChange, onCommit, error }: WidgetProps) {
  const { colorTokens } = useWidgetEnvironment();
  const token = themeToken(value);
  const mixed = isMixed(value);
  const disabled = prop.readonly === true;

  return (
    <span className="kn-field">
      <span className="kn-color">
        {mixed ? null : token === null ? (
          <input
            type="color"
            className="kn-color__picker"
            aria-label={`${label} color`}
            value={rgbToHex(isRgb(value) ? value : CUSTOM_DEFAULT)}
            disabled={disabled}
            onChange={(event) => {
              const parsed = parseHexRgb(event.target.value);
              if (parsed) onChange(parsed);
            }}
            onBlur={onCommit}
          />
        ) : (
          <span
            className="kn-color__swatch"
            aria-hidden="true"
            style={{ background: `var(--vp-${cssName(token)})` }}
          />
        )}
        <select
          id={id}
          className="kn-input kn-select"
          aria-label={label}
          value={mixed ? MIXED_OPTION : (token ?? "")}
          disabled={disabled}
          {...fieldAria(id, error)}
          onChange={(event) => {
            const next = event.target.value;
            onChange(next === "" ? CUSTOM_DEFAULT : { theme: next });
            onCommit();
          }}
        >
          <MixedOption mixed={mixed} />
          <option value="">Custom</option>
          {colorTokens.map((name) => (
            <option key={name} value={name}>
              Theme: {name}
            </option>
          ))}
        </select>
      </span>
      <FieldError id={id} error={error} />
    </span>
  );
}

function themeToken(value: unknown): string | null {
  if (typeof value !== "object" || value === null || !("theme" in value)) return null;
  return typeof value.theme === "string" ? value.theme : null;
}

function isRgb(value: unknown): value is Rgb {
  return Array.isArray(value) && value.length === 3 && value.every((n) => typeof n === "number");
}
